import { LinearProgress } from '@mui/material';
import { Box } from '@mui/system';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useI2pDataGridEdit, useI2pSimpleDataGrid } from '@price-for-profit/data-grid';
import { useMemo } from 'react';
import { ONE_HOUR, QUERY_KEYS } from 'shared/constants';
import { useUpdateMileageBucket } from 'shared/mutations';
import { useService } from 'shared/providers';
import { Freight, UserInfo } from 'shared/types';
import { determineIsAdmin } from 'shared/utility';
import { columnsFreight } from './table-def';
import { TableToolbar } from './table-toolbar';

interface TableProps {
  user: UserInfo;
}
export function Table({ user }: TableProps) {
  const isAdmin = determineIsAdmin(user);
  const { freightService } = useService();

  const columns = useMemo(() => {
    return columnsFreight({ admin: isAdmin });
  }, [isAdmin]);

  const { getDataGridProps, invalidate, updatedAt } = useI2pSimpleDataGrid<Freight>({
    columns,
    name: QUERY_KEYS.freight,
    getData: async () => {
      return freightService.getGridData();
    },
    queryOptions: {
      staleTime: ONE_HOUR,
    },
  });

  const mileageBucketMutation = useUpdateMileageBucket();

  const { getDataGridEditProps } = useI2pDataGridEdit<Freight>({
    onEdit: async (newRow, oldRow) => {
      const newRevisedMileageBucket = newRow.revisedMileageBucket;

      if (newRevisedMileageBucket !== oldRow.revisedMileageBucket) {
        const updatedRecords = await mileageBucketMutation.mutateAsync({
          mileageBucket: newRevisedMileageBucket,
          billToCustCode: newRow.billToCustCode,
          userId: user.id,
          userEmail: user.email,
        });

        const updatedRow = updatedRecords.filter(record => record.id === newRow.id)[0];
        return { ...updatedRow };
      }

      return oldRow;
    },
  });

  const styles = useMemo(
    () =>
      isAdmin
        ? {
            '& .drive-app-wps--cell--editable': {
              backgroundColor: '#dbe7f2',
            },
          }
        : undefined,
    [isAdmin]
  );
  return (
    <Box height='calc(100vh - 175px)'>
      <DataGridPro
        {...getDataGridProps()}
        {...getDataGridEditProps()}
        // custom density toolbar
        density='compact'
        localeText={{
          toolbarDensity: 'Row Spacing',
        }}
        components={{
          Toolbar: TableToolbar,
          LoadingOverlay: LinearProgress,
        }}
        componentsProps={{
          toolbar: { invalidate, updatedAt },
        }}
        disableSelectionOnClick
        treeData
        getTreeDataPath={row => row.hierarchy}
        groupingColDef={{
          headerName: 'Product Number',
        }}
        sx={{
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
            textOverflow: 'clip',
            whiteSpace: 'break-spaces',
            lineHeight: 1,
          },
          ...styles,
        }}
      />
    </Box>
  );
}
